import React, { useEffect, useMemo, useRef, useState } from "react";
import {
  Form,
  Input,
  Spin,
  Select,
  InputNumber,
  Divider,
  Radio,
  DatePicker,
  Tag,
  Space,
} from "antd";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import toast from "react-hot-toast";
import { save } from "../../../features/save/saveSlice";
import BreadCrumb from "../../../layout/BreadCrumb";
import {
  fetchCounties,
  fetchData,
  fetchLocation,
  fetchSubCounties,
  fetchSubLocation,
  fetchSubsidySource,
} from "../../../features/fetch/fetchSlice";
import CardLoading from "../../../components/CardLoading";
import { useWatch } from "antd/es/form/Form";
import dayjs from "dayjs";
import { normalizeDateToLocalYear } from "../../../utils";

const { TextArea } = Input;
const ProjectAdd = () => {
  const urlPathway = "api/v2/irrigationPathway/get";
  const urlProgramme = "api/v2/programs/get";
  const urlProjType = "api/v2/projectType/get";
  const urlThreshold = "api/v2/threshold/get";
  const urlAgency = "api/v2/agency/get";
  const urlScheme = "sch/get_schemes";

  const urlWard = "ward/fetch_wards";
  const urlConstituency = "constituency/fetch_constituencys";

  const [form] = Form.useForm();
  const formRef = useRef(null);
  const { saving } = useSelector((state) => state.save);
  const {
    counties,
    crops,
    subsidySources,
    subCounties,
    locations,
    subLocations,
  } = useSelector((state) => state.fetch);
  const {
    loading,
    activeObjOngoingProject,
    dataPathway,
    dataProgramme,
    dataProjType,
    dataAgency,
    dataScheme,
    dataConstituency,
    dataWard,
  } = useSelector((state) => ({
    activeObjOngoingProject: state.obj.activeObj["ongoingProject"],
    dataPathway: state.fetch.data[urlPathway] || [],
    dataProgramme: state.fetch.data[urlProgramme] || [],
    dataProjType: state.fetch.data[urlProjType] || [],
    dataAgency: state.fetch.data[urlAgency] || [],
    dataScheme: state.fetch.data[urlScheme] || [],
    loading: state.fetch.loading,
    dataWard: state.fetch.data[urlWard] || [],
    dataConstituency: state.fetch.data[urlConstituency] || [],
  }));

  const dispatch = useDispatch();
  const navigate = useNavigate();

  function handleCancel() {
    navigate(-1);
  }

  const plainOptions = ["Yes", "No"];

  const [projectCountyId, setProjectCountyId] = useState(
    activeObjOngoingProject?.projectCountyId ?? "",
  );
  const [projectConstituencyId, setProjectConstituencyId] = useState(
    activeObjOngoingProject?.projectConstituencyId ?? "",
  );
  const [projectSubCountyId, setProjectSubCountyId] = useState(
    activeObjOngoingProject?.projectSubCountyId ?? "",
  );
  const [projectLocationId, setProjectLocationId] = useState(
    activeObjOngoingProject?.projectLocationId ?? "",
  );
  const [projectSubLocationId, setProjectSubLocationId] = useState(
    activeObjOngoingProject?.projectSubLocationId ?? "",
  );
  const [projectWardId, setProjectWardId] = useState(
    activeObjOngoingProject?.projectWardId ?? "",
  );

  const handleProjectCountyChange = async (value) => {
    setProjectCountyId(value);
    setProjectConstituencyId("");
    setProjectSubCountyId("");
    setProjectLocationId("");
    setProjectSubLocationId("");
    setProjectWardId("");

    form.setFieldsValue({
      projectCountyId: value,
      projectConstituencyId: "",
      projectSubCountyId: "",
      projectLocationId: "",
      projectSubLocationId: "",
      projectWardId: "",
    });

    await dispatch(fetchSubCounties({ subCountyId: value }));
    await dispatch(fetchData({ url: urlConstituency, constCounty: value }));
  };

  const handleProjectConstituencyChange = async (value) => {
    setProjectConstituencyId(value);
    setProjectWardId("");

    form.setFieldsValue({
      projectConstituencyId: value,
      projectWardId: "",
    });

    await dispatch(fetchData({ url: urlWard, wdConst: value }));
  };

  const handleProjectSubCountyChange = async (value) => {
    setProjectSubCountyId(value);
    setProjectLocationId("");
    setProjectSubLocationId("");

    form.setFieldsValue({
      projectSubCountyId: value,
      projectLocationId: "",
      projectSubLocationId: "",
    });

    await dispatch(fetchLocation({ locSubId: value }));
  };

  const handleProjectLocationChange = async (value) => {
    setProjectLocationId(value);
    setProjectSubLocationId("");

    form.setFieldsValue({
      projectLocationId: value,
      projectSubLocationId: "",
    });

    await dispatch(fetchSubLocation({ slocLocId: value }));
  };

  const handleProjectSubLocationChange = (value) => {
    setProjectSubLocationId(value);
    form.setFieldsValue({ projectSubLocationId: value });
  };

  const handleProjectWardChange = (value) => {
    setProjectWardId(value);
    form.setFieldsValue({ projectWardId: value });
  };

  const onFinish = async (data) => {
    data.projectStatus = "ONGOING";
    data.projectId = activeObjOngoingProject?.projectId
      ? activeObjOngoingProject?.projectId
      : null;
    data.projectSourceOfFundsId = Array.isArray(data?.projectSourceOfFundsId)
      ? data?.projectSourceOfFundsId.join(",")
      : data?.projectSourceOfFundsId;
    data.projectStartDate = normalizeDateToLocalYear(data.projectStartDate);
    data.projectEndDate = normalizeDateToLocalYear(data.projectEndDate);
    data.projectPerformanceBondStartDate = normalizeDateToLocalYear(
      data.projectPerformanceBondStartDate,
    );
    data.projectPerformanceBondEndDate = normalizeDateToLocalYear(
      data.projectPerformanceBondEndDate,
    );

    data.projectRevisedEndDate = normalizeDateToLocalYear(
      data.projectRevisedEndDate,
    );
    data.projectContractAwardDate = normalizeDateToLocalYear(
      data.projectContractAwardDate,
    );
    data.projectAdvancedPaymentDate = normalizeDateToLocalYear(
      data.projectAdvancedPaymentDate,
    );

    const res = await dispatch(
      save({
        ...data,
        url: "api/v2/main_projects/save",
      }),
    );
    if (res?.payload?.success) {
      await toast.success(res?.payload?.message);
      await form.resetFields();
      await handleCancel();
    } else {
      toast.error(res?.payload?.message);
    }
  };

  useEffect(() => {
    if (activeObjOngoingProject?.projectSourceOfFundsId) {
      form.setFieldsValue({
        projectSourceOfFundsId: activeObjOngoingProject?.projectSourceOfFundsId
          ?.split(",")
          .map((b) => b.trim()),
      });
    }
  }, [activeObjOngoingProject, form]);

  const initialFormValues = {
    ...activeObjOngoingProject,
    projectStartDate: activeObjOngoingProject?.projectStartDate
      ? dayjs(new Date(activeObjOngoingProject?.projectStartDate))
      : null,
    projectEndDate: activeObjOngoingProject?.projectEndDate
      ? dayjs(new Date(activeObjOngoingProject?.projectEndDate))
      : null,
    projectRevisedEndDate: activeObjOngoingProject?.projectRevisedEndDate
      ? dayjs(new Date(activeObjOngoingProject?.projectRevisedEndDate))
      : null,
    projectContractAwardDate: activeObjOngoingProject?.projectContractAwardDate
      ? dayjs(new Date(activeObjOngoingProject?.projectContractAwardDate))
      : null,
    projectPerformanceBondStartDate:
      activeObjOngoingProject?.projectPerformanceBondStartDate
        ? dayjs(
            new Date(activeObjOngoingProject?.projectPerformanceBondStartDate),
          )
        : null,
    projectPerformanceBondEndDate:
      activeObjOngoingProject?.projectPerformanceBondEndDate
        ? dayjs(
            new Date(activeObjOngoingProject?.projectPerformanceBondEndDate),
          )
        : null,

    projectAdvancedPaymentDate:
      activeObjOngoingProject?.projectAdvancedPaymentDate
        ? dayjs(new Date(activeObjOngoingProject?.projectAdvancedPaymentDate))
        : null,
  };

  useEffect(() => {
    dispatch(fetchCounties());
    dispatch(
      fetchData({
        url: urlPathway,
        limit: null,
        start: 0,
      }),
    );
    dispatch(
      fetchData({
        url: urlProgramme,
        limit: null,
        start: 0,
      }),
    );

    dispatch(
      fetchData({
        url: urlProjType,
        limit: null,
        start: 0,
      }),
    );

    dispatch(
      fetchData({
        url: urlAgency,
        limit: null,
        start: 0,
      }),
    );
    dispatch(
      fetchData({
        url: urlScheme,
        limit: null,
        start: 0,
      }),
    );
    dispatch(fetchSubsidySource());
  }, []);

  useEffect(() => {
    if (activeObjOngoingProject?.projectId) {
      dispatch(
        fetchSubCounties({
          subCountyId: activeObjOngoingProject?.projectCountyId,
        }),
      );
      dispatch(
        fetchData({
          url: urlConstituency,
          constCounty: activeObjOngoingProject?.projectCountyId,
        }),
      );
      dispatch(
        fetchData({
          url: urlWard,
          wdConst: activeObjOngoingProject?.projectConstituencyId,
        }),
      );
      dispatch(
        fetchLocation({
          locSubId: activeObjOngoingProject?.projectSubCountyId,
        }),
      );
      dispatch(
        fetchSubLocation({
          slocLocId: activeObjOngoingProject?.projectLocationId,
        }),
      );
    }
  }, [activeObjOngoingProject, form]);

  const isReadOnly =
    activeObjOngoingProject?.projectStatus === "COMPLETED" ||
    activeObjOngoingProject?.projectStatus === "STALLED";
  const projAssoc = useWatch("projectIsSchemeAssociated", form);
  const projectHasAdvancePayment = useWatch("projectHasAdvancePayment", form);

  const fundingSources = Form.useWatch("projectSourceOfFundsId", form);

  const projectTypeId = useWatch("projectTypeId", form);
  const selectedProjectType = useMemo(() => {
    return dataProjType?.find((item) => item?.projectTypeId === projectTypeId);
  }, [projectTypeId, dataProjType]);
  const governmentSourceId = subsidySources?.find((item) =>
    item.subsidySourceName?.toLowerCase().includes("government"),
  )?.subsidySourceId;

  const donorSourceId = subsidySources?.find((item) =>
    item.subsidySourceName?.toLowerCase().includes("development"),
  )?.subsidySourceId;

  const bankSourceId = subsidySources?.find((item) =>
    item.subsidySourceName?.toLowerCase().includes("bank"),
  )?.subsidySourceId;

  return (
    <div className="w-full ">
      <BreadCrumb
        breadcrumbs={[
          { title: "Projects", path: "back" },
          { title: "Project Add Form" },
        ]}
      />
      <div className="white_card mt-[2.06rem]">
        <div className="form-add-h mb-[1.25rem]">
          {activeObjOngoingProject?.projectId
            ? "Update Project"
            : "Register a New Project"}{" "}
        </div>
        <Divider />
        <div className="w-full mt-[1.75rem]">
          <Form
            layout="vertical"
            ref={formRef}
            name="control-ref"
            onFinish={onFinish}
            className=""
            style={{
              maxWidth: "100%",
            }}
            form={form}
            initialValues={initialFormValues}
          >
            <div className="custom-title">Project Details</div>
            <div className="grid lg:grid-cols-2 grid-cols-1 lg:gap-x-10">
              <Form.Item
                name="projectNumber"
                label={"Project Number"}
                rules={[
                  {
                    required: true,
                    message: "Please add project number",
                  },
                ]}
              >
                <Input disabled={isReadOnly} className="input" />
              </Form.Item>
              <Form.Item
                name="projectName"
                label={"Project Name"}
                rules={[
                  {
                    required: true,
                    message: "Please add project name",
                  },
                ]}
              >
                <Input className="input" disabled={isReadOnly} />
              </Form.Item>

              <Form.Item
                name="projectScope"
                label={"Project Scope"}
                rules={[
                  {
                    required: true,
                    message: "Please add project scope",
                  },
                ]}
              >
                <TextArea disabled={isReadOnly} className="text_area" />
              </Form.Item>

              <Form.Item
                name="projectPurpose"
                label={"Project Purpose"}
                rules={[
                  {
                    required: true,
                    message: "Please add project purpose",
                  },
                ]}
              >
                <TextArea disabled={isReadOnly} className="text_area" />
              </Form.Item>
              <Form.Item
                name="projectPathwayId"
                className=""
                label={"Pathway"}
                rules={[
                  {
                    required: true,
                    message: "Please select pathway",
                  },
                ]}
              >
                <Select
                  className=""
                  disabled={isReadOnly}
                  allowClear
                  style={{
                    width: "100%",
                  }}
                  options={
                    dataPathway?.length > 0 &&
                    dataPathway?.map((item) => ({
                      value: item?.irrigationPathwayId,
                      label: item?.irrigationPathwayName,
                    }))
                  }
                  showSearch
                  optionFilterProp="children"
                  filterOption={(input, option) =>
                    (option?.label?.toLocaleLowerCase() ?? "").includes(
                      input?.toLocaleLowerCase(),
                    )
                  }
                />
              </Form.Item>
              <Form.Item
                name="projectProgramId"
                className=""
                label={"Program"}
                rules={[
                  {
                    required: true,
                    message: "Please select program",
                  },
                ]}
              >
                <Select
                  className=""
                  disabled={isReadOnly}
                  allowClear
                  style={{
                    width: "100%",
                  }}
                  options={
                    dataProgramme?.length > 0 &&
                    dataProgramme?.map((item) => ({
                      value: item?.programId,
                      label: item?.programName,
                    }))
                  }
                  showSearch
                  optionFilterProp="children"
                  filterOption={(input, option) =>
                    (option?.label?.toLocaleLowerCase() ?? "").includes(
                      input?.toLocaleLowerCase(),
                    )
                  }
                />
              </Form.Item>
              <Form.Item
                name="projectTypeId"
                className=""
                label={"Project Type"}
                rules={[
                  {
                    required: true,
                    message: "Please select project type",
                  },
                ]}
              >
                <Select
                  className=""
                  disabled={isReadOnly}
                  allowClear
                  style={{
                    width: "100%",
                  }}
                  options={
                    dataProjType?.length > 0 &&
                    dataProjType?.map((item) => ({
                      value: item?.projectTypeId,
                      label: item?.projectTypeName,
                    }))
                  }
                  showSearch
                  optionFilterProp="children"
                  filterOption={(input, option) =>
                    (option?.label?.toLocaleLowerCase() ?? "").includes(
                      input?.toLocaleLowerCase(),
                    )
                  }
                />
              </Form.Item>
              <Form.Item
                name="projectThresholdName"
                className=""
                label={"Threshhold"}
                rules={[
                  {
                    required: false,
                    message: "Please select threshhold",
                  },
                ]}
              >
                <Input disabled className="input" />
              </Form.Item>
              <Form.Item
                name="projectEngineerName"
                className=""
                label={"Individual/Engineer in Charge"}
                rules={[
                  {
                    required: true,
                    message: "Please give individual/Engineer in charge",
                  },
                ]}
              >
                <Input className="input" disabled={isReadOnly} />
              </Form.Item>
              <Form.Item
                name="projectAgencyId"
                className=""
                label={"Implementing Entity/Department"}
                rules={[
                  {
                    required: true,
                    message: "Please select Implementing Entity/Department",
                  },
                ]}
              >
                <Select
                  className=""
                  disabled={isReadOnly}
                  allowClear
                  style={{
                    width: "100%",
                  }}
                  options={
                    dataAgency?.length > 0 &&
                    dataAgency?.map((item) => ({
                      value: item?.agencyId,
                      label: item?.agencyName,
                    }))
                  }
                  showSearch
                  optionFilterProp="children"
                  filterOption={(input, option) =>
                    (option?.label?.toLocaleLowerCase() ?? "").includes(
                      input?.toLocaleLowerCase(),
                    )
                  }
                />
              </Form.Item>
              <Form.Item
                name="projectContractor"
                className=""
                label={"Contractor"}
                rules={[
                  {
                    required: true,
                    message: "Please give contractor",
                  },
                ]}
              >
                <Input className="input" disabled={isReadOnly} />
              </Form.Item>
              <Form.Item
                name="projectIsSchemeAssociated"
                className=""
                label={"Associated to Scheme"}
                rules={[
                  {
                    required: true,
                    message: "Please select if associated with a scheme",
                  },
                ]}
              >
                <Radio.Group disabled={isReadOnly} options={plainOptions} />
              </Form.Item>

              {projAssoc === "Yes" && (
                <Form.Item
                  name="projectSchemeAssociatedId"
                  className=""
                  label={"Select Associated Scheme"}
                  rules={[
                    {
                      required: true,
                      message: "Please select associated scheme",
                    },
                  ]}
                >
                  <Select
                    disabled={isReadOnly}
                    className=""
                    allowClear
                    style={{
                      width: "100%",
                    }}
                    options={
                      dataScheme?.length > 0 &&
                      dataScheme?.map((item) => ({
                        value: item?.schId,
                        label: item?.schName,
                      }))
                    }
                    showSearch
                    optionFilterProp="children"
                    filterOption={(input, option) =>
                      (option?.label?.toLocaleLowerCase() ?? "").includes(
                        input?.toLocaleLowerCase(),
                      )
                    }
                  />
                </Form.Item>
              )}

              <Form.Item
                name="projectCountyId"
                label="County"
                rules={[{ required: true, message: "Please select county" }]}
              >
                <Select
                  allowClear
                  options={counties?.map((c) => ({
                    value: c?.countyId,
                    label: c?.countyName,
                  }))}
                  onChange={handleProjectCountyChange}
                  showSearch
                  filterOption={(input, option) =>
                    option?.label?.toLowerCase()?.includes(input.toLowerCase())
                  }
                />
              </Form.Item>

              {projectCountyId && (
                <>
                  <Form.Item
                    name="projectConstituencyId"
                    label="Constituency"
                    rules={[
                      {
                        required: false,
                        message: "Please select constituency",
                      },
                    ]}
                  >
                    <Select
                      allowClear
                      options={dataConstituency?.map((c) => ({
                        value: c?.constId,
                        label: c?.constName,
                      }))}
                      onChange={handleProjectConstituencyChange}
                      showSearch
                      filterOption={(input, option) =>
                        option?.label
                          ?.toLowerCase()
                          ?.includes(input.toLowerCase())
                      }
                    />
                  </Form.Item>

                  <Form.Item
                    name="projectSubCountyId"
                    label="Subcounty"
                    rules={[
                      { required: false, message: "Please select subcounty" },
                    ]}
                  >
                    <Select
                      allowClear
                      options={subCounties?.map((s) => ({
                        value: s?.subId,
                        label: s?.subCountyName,
                      }))}
                      onChange={handleProjectSubCountyChange}
                      showSearch
                      filterOption={(input, option) =>
                        option?.label
                          ?.toLowerCase()
                          ?.includes(input.toLowerCase())
                      }
                    />
                  </Form.Item>
                </>
              )}

              {projectSubCountyId && (
                <Form.Item
                  name="projectLocationId"
                  label="Location"
                  rules={[
                    { required: false, message: "Please select location" },
                  ]}
                >
                  <Select
                    allowClear
                    options={locations?.map((l) => ({
                      value: l?.locId,
                      label: l?.locLocation,
                    }))}
                    onChange={handleProjectLocationChange}
                    showSearch
                    filterOption={(input, option) =>
                      option?.label
                        ?.toLowerCase()
                        ?.includes(input.toLowerCase())
                    }
                  />
                </Form.Item>
              )}

              {projectLocationId && (
                <Form.Item
                  name="projectSubLocationId"
                  label="Sublocation"
                  rules={[
                    { required: false, message: "Please select sublocation" },
                  ]}
                >
                  <Select
                    allowClear
                    options={subLocations?.map((sl) => ({
                      value: sl?.slocId,
                      label: sl?.slocLocation,
                    }))}
                    onChange={handleProjectSubLocationChange}
                    showSearch
                    filterOption={(input, option) =>
                      option?.label
                        ?.toLowerCase()
                        ?.includes(input.toLowerCase())
                    }
                  />
                </Form.Item>
              )}

              {projectConstituencyId && (
                <Form.Item
                  name="projectWardId"
                  label="Ward"
                  rules={[{ required: false, message: "Please select ward" }]}
                >
                  <Select
                    allowClear
                    options={dataWard?.map((w) => ({
                      value: w?.id,
                      label: w?.wdWard,
                    }))}
                    onChange={handleProjectWardChange}
                    showSearch
                    filterOption={(input, option) =>
                      option?.label
                        ?.toLowerCase()
                        ?.includes(input.toLowerCase())
                    }
                  />
                </Form.Item>
              )}

              <Form.Item
                name="projectLatitude"
                label={"Latitude (Decimal degrees, WGS84)"}
                rules={[
                  {
                    required: true,
                    message: "Please add latitude",
                  },
                ]}
              >
                <InputNumber
                  disabled={isReadOnly}
                  parser={(value) => value?.replace(/\$\s?|(,*)/g, "")}
                  className="input flex"
                />
              </Form.Item>
              <Form.Item
                name="projectLongitude"
                label={"Longitude (Decimal degrees, WGS84)"}
                rules={[
                  {
                    required: true,
                    message: "Please add longitude",
                  },
                ]}
              >
                <InputNumber
                  disabled={isReadOnly}
                  parser={(value) => value?.replace(/\$\s?|(,*)/g, "")}
                  className="input flex"
                />
              </Form.Item>
              <Form.Item
                label="Commencement Date"
                name="projectStartDate"
                rules={[
                  {
                    required: true,
                    message: "Commencement date is required",
                  },
                ]}
              >
                <DatePicker
                  format={"YYYY MMM DD"}
                  className="input"
                  disabled={isReadOnly}
                />
              </Form.Item>
              <Form.Item
                label="Expected End Date"
                name="projectEndDate"
                rules={[
                  {
                    required: true,
                    message: "Expected end date is required",
                  },
                ]}
              >
                <DatePicker
                  format={"YYYY MMM DD"}
                  className="input"
                  disabled={isReadOnly}
                />
              </Form.Item>
              <Form.Item
                label="Revised End Date"
                name="projectRevisedEndDate"
                rules={[
                  {
                    required: false,
                    message: "Revised end date is required",
                  },
                ]}
              >
                <DatePicker
                  format={"YYYY MMM DD"}
                  className="input"
                  disabled={isReadOnly}
                />
              </Form.Item>

              <Form.Item
                label="Award Date"
                name="projectContractAwardDate"
                rules={[
                  {
                    required: false,
                    message: "Award date is required",
                  },
                ]}
              >
                <DatePicker
                  format={"YYYY MMM DD"}
                  className="input"
                  disabled={isReadOnly}
                />
              </Form.Item>
              <Form.Item
                name="projectDefectLiabilityPeriod"
                label={"Defect Liability Period (Days)"}
                rules={[
                  {
                    required: false,
                    message: "Please add Defect Liability Period",
                  },
                ]}
              >
                <InputNumber
                  min={0}
                  disabled={isReadOnly}
                  formatter={(value) =>
                    `${value}`.replace(/\B(?=(\d{3})+(?!\d))/g, ",")
                  }
                  parser={(value) => value?.replace(/\$\s?|(,*)/g, "")}
                  className="input flex"
                />
              </Form.Item>

              <Form.Item
                label="Performace Bond Start Date"
                name="projectPerformanceBondStartDate"
                rules={[
                  {
                    required: false,
                    message: "Performace bond date is required",
                  },
                ]}
              >
                <DatePicker
                  format={"YYYY MMM DD"}
                  className="input"
                  disabled={isReadOnly}
                />
              </Form.Item>

              <Form.Item
                label="Performace Bond End Date"
                name="projectPerformanceBondEndDate"
                rules={[
                  {
                    required: false,
                    message: "Performace bond end date is required",
                  },
                ]}
              >
                <DatePicker
                  format={"YYYY MMM DD"}
                  className="input"
                  disabled={isReadOnly}
                />
              </Form.Item>
            </div>

            <div className="custom-title mt-[2.5rem]">Project Cost</div>
            <div className="grid lg:grid-cols-2 grid-cols-1 lg:gap-x-10">
              {/* <Form.Item
                label="Source of Funding"
                name="projectSourceOfFundsId"
                rules={[
                  { required: false, message: "Source of Funding is required" },
                ]}
              >
                <Select
                  onChange={() => {
                    form.setFieldsValue({ projectCost: undefined });
                  }}
                  disabled={isReadOnly}
                  mode="multiple"
                  allowClear
                  showSearch
                  dropdownRender={(menu) => (
                    <>
                      <Space style={{ padding: "8px 8px 4px" }}>
                        <span className="select-info mt-1">
                          Choose one or more of the following sources
                        </span>
                      </Space>
                      <Divider style={{ margin: "8px 0" }} />
                      {menu}
                    </>
                  )}
                  optionFilterProp="children"
                  filterOption={(input, option) =>
                    (option?.children?.toLowerCase() ?? "").includes(
                      input.toLowerCase()
                    )
                  }
                  tagRender={(props) => {
                    const { label, closable, onClose } = props;
                    return (
                      <span
                        style={{
                          display: "inline-flex",
                          alignItems: "center",
                          padding: "4px 8px",
                          margin: "2px",
                          borderRadius: "4px",
                          background: "#f5f5f5",
                        }}
                      >
                        {label}
                        {closable && (
                          <span
                            onClick={onClose}
                            style={{
                              marginLeft: "8px",
                              cursor: "pointer",
                              color: "red",
                            }}
                          >
                            ×
                          </span>
                        )}
                      </span>
                    );
                  }}
                  dropdownStyle={{ maxHeight: 400, overflowY: "auto" }}
                >
                  {subsidySources?.map((item) => (
                    <Select.Option
                      key={item?.subsidySourceId}
                      value={item?.subsidySourceId}
                    >
                      {item?.subsidySourceName}
                    </Select.Option>
                  ))}
                </Select>
              </Form.Item>

              {fundingSources?.includes(governmentSourceId) && (
                <Form.Item
                  name="projectGovernmentAmount"
                  label={"Government Funding"}
                  rules={[
                    {
                      required: true,
                      message: "Please add total government funding",
                    },
                  ]}
                >
                  <InputNumber
                    onChange={() => {
                      form.setFieldsValue({ projectCost: undefined });
                    }}
                    min={0}
                    disabled={isReadOnly}
                    formatter={(value) =>
                      `${value}`.replace(/\B(?=(\d{3})+(?!\d))/g, ",")
                    }
                    parser={(value) => value?.replace(/\$\s?|(,*)/g, "")}
                    placeholder="Enter value"
                    className="input flex"
                  />
                </Form.Item>
              )}

              {fundingSources?.includes(donorSourceId) && (
                <Form.Item
                  name="projectDonorsAmount"
                  label={"Donors Funding"}
                  rules={[
                    {
                      required: true,
                      message: "Please add total donors funding",
                    },
                  ]}
                >
                  <InputNumber
                    onChange={() => {
                      form.setFieldsValue({ projectCost: undefined });
                    }}
                    min={0}
                    disabled={isReadOnly}
                    formatter={(value) =>
                      `${value}`.replace(/\B(?=(\d{3})+(?!\d))/g, ",")
                    }
                    parser={(value) => value?.replace(/\$\s?|(,*)/g, "")}
                    placeholder="Enter value"
                    className="input flex"
                  />
                </Form.Item>
              )}

              {fundingSources?.includes(bankSourceId) && (
                <Form.Item
                  name="projectBankAmount"
                  label={"Bank Funding"}
                  rules={[
                    {
                      required: true,
                      message: "Please add total bank funding",
                    },
                  ]}
                >
                  <InputNumber
                    onChange={() => {
                      form.setFieldsValue({ projectCost: undefined });
                    }}
                    min={0}
                    disabled={isReadOnly}
                    formatter={(value) =>
                      `${value}`.replace(/\B(?=(\d{3})+(?!\d))/g, ",")
                    }
                    parser={(value) => value?.replace(/\$\s?|(,*)/g, "")}
                    placeholder="Enter value"
                    className="input flex"
                  />
                </Form.Item>
              )} */}

              <Form.Item
                name="projectCost"
                label={"Total Project Cost"}
                rules={[
                  {
                    required: true,
                    message: "Please add total project cost",
                  },
                  // {
                  //   validator: async (_, value) => {
                  //     const fundingSources =
                  //       form?.getFieldValue("projectSourceOfFundsId") || [];
                  //     const govAmount =
                  //       form?.getFieldValue("projectGovernmentAmount") ?? 0;
                  //     const donorAmount =
                  //       form?.getFieldValue("projectDonorsAmount") ?? 0;
                  //     const bankAmount =
                  //       form?.getFieldValue("projectBankAmount") ?? 0;
                  //     const hasGov =
                  //       fundingSources?.includes(governmentSourceId);
                  //     const hasDonor = fundingSources?.includes(donorSourceId);
                  //     const hasBank = fundingSources?.includes(bankSourceId);

                  //     let expected = 0;

                  //     if (hasGov && hasDonor && hasBank) {
                  //       expected = govAmount + donorAmount + bankAmount;
                  //     } else if (hasGov && hasDonor) {
                  //       expected = govAmount + donorAmount;
                  //     } else if (hasGov && hasBank) {
                  //       expected = govAmount + bankAmount;
                  //     } else if (hasDonor && hasBank) {
                  //       expected = donorAmount + bankAmount;
                  //     } else if (hasGov) {
                  //       expected = govAmount;
                  //     } else if (hasDonor) {
                  //       expected = donorAmount;
                  //     } else if (hasBank) {
                  //       expected = bankAmount;
                  //     } else {
                  //       return Promise.reject(
                  //         "Please select a funding source.",
                  //       );
                  //     }

                  //     if (expected !== value) {
                  //       return Promise.reject(
                  //         "Total project cost must match the sum of the funding amounts.",
                  //       );
                  //     }

                  //     return Promise.resolve();
                  //   },
                  // },
                ]}
              >
                <InputNumber
                  min={0}
                  disabled={isReadOnly}
                  formatter={(value) =>
                    `${value}`.replace(/\B(?=(\d{3})+(?!\d))/g, ",")
                  }
                  parser={(value) => value?.replace(/\$\s?|(,*)/g, "")}
                  placeholder="Enter value"
                  className="input flex"
                />
              </Form.Item>

              <Form.Item
                name="projectRevisedCost"
                label={"Revised Project Cost"}
                rules={[
                  {
                    required: false,
                    message: "Please add revised project cost",
                  },
                ]}
              >
                <InputNumber
                  min={0}
                  disabled={isReadOnly}
                  formatter={(value) =>
                    `${value}`.replace(/\B(?=(\d{3})+(?!\d))/g, ",")
                  }
                  parser={(value) => value?.replace(/\$\s?|(,*)/g, "")}
                  className="input flex"
                />
              </Form.Item>

              <Form.Item
                name="projectHasAdvancePayment"
                className=""
                label={"Do you have advanced payment ? "}
                rules={[
                  {
                    required: true,
                    message: "Please select if you have advanced payment",
                  },
                ]}
              >
                <Radio.Group
                  onChange={(e) => {
                    form.setFieldValue("projectAdvancedPayment", null);
                    form.setFieldValue("projectAdvancedPaymentDate", null);
                  }}
                  disabled={isReadOnly}
                  options={plainOptions}
                />
              </Form.Item>

              {projectHasAdvancePayment === "Yes" && (
                <>
                  <Form.Item
                    name="projectAdvancedPayment"
                    label={"Advance Payment"}
                    rules={[
                      {
                        required: true,
                        message: "Please add advance payment",
                      },
                      {
                        validator: async (_, value) => {
                          const projectCost =
                            form?.getFieldValue("projectCost") ?? 0;
                          if (value > projectCost) {
                            return Promise.reject(
                              "Advance payment cannot exceed total project cost",
                            );
                          }
                          return Promise.resolve();
                        },
                      },
                    ]}
                  >
                    <InputNumber
                      min={0}
                      disabled={isReadOnly}
                      formatter={(value) =>
                        `${value}`.replace(/\B(?=(\d{3})+(?!\d))/g, ",")
                      }
                      parser={(value) => value?.replace(/\$\s?|(,*)/g, "")}
                      className="input flex"
                    />
                  </Form.Item>
                  <Form.Item
                    name="projectAdvancedPaymentDate"
                    rules={[
                      {
                        required: true,
                        message: "Advance payment date is required",
                      },
                    ]}
                    label="Advance Payment Date"
                  >
                    <DatePicker format={"YYYY MMM DD"} className="input" />
                  </Form.Item>
                </>
              )}
            </div>
            <div className="custom-title mt-[2.5rem]">
              Expected Deliverables
            </div>
            <div className="grid lg:grid-cols-2 grid-cols-1 lg:gap-x-10">
              <Form.Item
                name="projectWaterVolume"
                label={
                  selectedProjectType?.projectTypeName === "Borehole"
                    ? "Yield of Borehole (M³/hr)"
                    : "Volume of Water (M³)"
                }
                rules={[
                  {
                    required: false,
                    message: "Please add volume of water (M³)",
                  },
                ]}
              >
                <InputNumber
                  suffix={
                    <span>
                      M<sup>3</sup>
                    </span>
                  }
                  min={0}
                  disabled={isReadOnly}
                  parser={(value) => value?.replace(/\$\s?|(,*)/g, "")}
                  className="input flex"
                />
              </Form.Item>
              <Form.Item
                name="projectAreaTarget"
                label={"Targeted Area Under Irrigation"}
                rules={[
                  {
                    required: true,
                    message: "Please add targeted area under irrigation",
                  },
                ]}
              >
                <InputNumber
                  suffix="Acre"
                  min={0}
                  disabled={isReadOnly}
                  parser={(value) => value?.replace(/\$\s?|(,*)/g, "")}
                  className="input flex"
                />
              </Form.Item>
              <Form.Item
                name="projectPotentialBeneficiaries"
                label={"Potential Beneficiaries"}
                rules={[
                  {
                    required: true,
                    message: "Please add potential beneficiaries",
                  },
                ]}
              >
                <InputNumber
                  min={0}
                  disabled={isReadOnly}
                  parser={(value) => value?.replace(/\$\s?|(,*)/g, "")}
                  className="input flex"
                />
              </Form.Item>

              <Form.Item
                name="projectLiveStockBeneficially"
                label={"Livestock Potential Beneficiaries"}
                rules={[
                  {
                    required: false,
                    message: "Please add Livestock potential beneficiaries",
                  },
                ]}
              >
                <InputNumber
                  min={0}
                  disabled={isReadOnly}
                  parser={(value) => value?.replace(/\$\s?|(,*)/g, "")}
                  className="input flex"
                />
              </Form.Item>

              <Form.Item
                name="projectRemarks"
                label={"Remarks"}
                rules={[
                  {
                    required: true,
                    message: "Please add remarks",
                  },
                ]}
              >
                <TextArea className="text_area" disabled={isReadOnly} />
              </Form.Item>
            </div>

            <div className="flex justify-between mt-[48px] ">
              <div className="justify-start"></div>
              <div className="justify-end flex items-center mb-[58px] gap-x-5">
                <div className="w-[150px]">
                  <button
                    type="button"
                    key="back"
                    onClick={handleCancel}
                    className="cstm-btn"
                  >
                    Cancel
                  </button>
                </div>

                {activeObjOngoingProject?.projectStatus ===
                "COMPLETED" ? null : (
                  <div className="w-[150px]">
                    <button key="submit" type="submit" className="cstm-btn-2">
                      {saving ? (
                        <Spin />
                      ) : activeObjOngoingProject?.projectId ? (
                        "Update"
                      ) : (
                        "Save"
                      )}
                    </button>
                  </div>
                )}
              </div>
            </div>
          </Form>
        </div>
      </div>
    </div>
  );
};
export default ProjectAdd;
